/**
 * Cache Manager with Build Versioning
 *
 * Automatically clears sessionStorage and localStorage when a new build is deployed.
 * This prevents stale data and JS errors from incompatible cached data structures.
 *
 * Best practices:
 * 1. All storage keys are prefixed with build hash
 * 2. Old storage keys are automatically cleared on version change
 * 3. Session data is build-specific
 */

import { getShortCommitHash } from './build-info';

const STORAGE_VERSION_KEY = 'app_build_version';
const CURRENT_BUILD = getShortCommitHash();

/**
 * Clear cache and preserve auth tokens
 */
const clearCacheAndPreserveAuth = () => {
  console.log('[CacheManager] Clearing old cache and session data...');

  // Clear all sessionStorage
  sessionStorage.clear();

  // Clear all localStorage except critical auth tokens
  const keysToPreserve = ['auth_token', 'refresh_token', 'access_token', 'user'];
  const itemsToPreserve: Record<string, string> = {};

  keysToPreserve.forEach(key => {
    const value = localStorage.getItem(key);
    if (value) {
      itemsToPreserve[key] = value;
    }
  });

  // Clear everything
  localStorage.clear();

  // Restore preserved items
  Object.entries(itemsToPreserve).forEach(([key, value]) => {
    localStorage.setItem(key, value);
  });

  // Set new version
  localStorage.setItem(STORAGE_VERSION_KEY, CURRENT_BUILD);

  console.log('[CacheManager] Cache cleared successfully');
};

/**
 * Check if version has changed and handle update
 */
const checkVersionUpdate = () => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return false;
  }

  const storedVersion = localStorage.getItem(STORAGE_VERSION_KEY);

  if (storedVersion !== CURRENT_BUILD) {
    console.log(`[CacheManager] Build version changed: ${storedVersion} → ${CURRENT_BUILD}`);
    clearCacheAndPreserveAuth();
    return true;
  }

  return false;
};

/**
 * Initialize cache manager - call this once on app startup
 * Clears old storage if build version changed
 */
export const initCacheManager = () => {
  // Check version on page load
  const versionChanged = checkVersionUpdate();

  // Setup visibility change listener to check version when user returns to tab
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        // Tab became visible - check if version changed
        const needsReload = checkVersionUpdate();
        if (needsReload) {
          // Show notification to user about new version
          console.log('[CacheManager] New version detected - page will reload in 2 seconds...');

          // Show a subtle notification (using browser's built-in notification if available)
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('FormTo.cc updated', {
              body: 'Page will reload to apply updates',
              icon: '/favicon.png',
              tag: 'version-update'
            });
          }

          // Reload page after brief delay
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
      }
    });
  }

  return versionChanged;
};

/**
 * Get versioned storage key
 */
export const getVersionedKey = (key: string): string => {
  return `${CURRENT_BUILD}_${key}`;
};

/**
 * Session storage helpers with automatic versioning
 */
export const versionedSessionStorage = {
  getItem: (key: string): string | null => {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage.getItem(getVersionedKey(key));
  },

  setItem: (key: string, value: string): void => {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(getVersionedKey(key), value);
  },

  removeItem: (key: string): void => {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.removeItem(getVersionedKey(key));
  },

  clear: (): void => {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.clear();
  },
};

/**
 * Local storage helpers with automatic versioning
 */
export const versionedLocalStorage = {
  getItem: (key: string): string | null => {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(getVersionedKey(key));
  },

  setItem: (key: string, value: string): void => {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(getVersionedKey(key), value);
  },

  removeItem: (key: string): void => {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(getVersionedKey(key));
  },
};

/**
 * Get current build version
 */
export const getCurrentBuild = (): string => {
  return CURRENT_BUILD;
};

/**
 * Log build info to console (for debugging)
 */
export const logBuildInfo = () => {
  console.log(`%c🚀 FormTo.cc - Build ${CURRENT_BUILD}`, 'color: #10b981; font-weight: bold; font-size: 14px;');
  console.log('%cBuild info available via: window.__BUILD_INFO__', 'color: #64748b; font-size: 12px;');
};

// Expose build info globally for debugging
if (typeof window !== 'undefined') {
  (window as any).__BUILD_INFO__ = {
    version: CURRENT_BUILD,
    getStorageKeys: () => {
      const keys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) keys.push(key);
      }
      return keys;
    },
    clearCache: () => {
      localStorage.removeItem(STORAGE_VERSION_KEY);
      initCacheManager();
    },
  };
}
